import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiSolidEdit } from "react-icons/bi";
import { BsTrash3Fill } from "react-icons/bs";
import { IoArrowBackSharp, IoReceiptSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { Button, Table, UncontrolledTooltip } from "reactstrap";
import swal from "sweetalert";
import {
  applyClassToCharacters,
  convertToDateString,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetLanguageString, NoResult } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import { viewUsraReceiptPdf } from "../../shared/receiptViewer";
import { ApiFund } from "../ApiFund";
import EditUsraInfaq from "./EditUsraInfaq";
import ManageUsraInfaqFilters from "./ManageUsraInfaqFilters";

const cellStyle = {
  padding: "12px 14px",
  color: "#676a6c",
  verticalAlign: "middle",
};
const centerCell = { ...cellStyle, textAlign: "center" };
const getHeaderStyle = (isUrduUi) => ({
  padding: "12px 14px",
  color: "#2f4050",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: isUrduUi ? "0" : "0.3px",
  textTransform: isUrduUi ? "none" : "uppercase",
  background: "#f9fafb",
  borderBottom: "1px solid #e7eaec",
});
const centerHeader = (isUrduUi) => ({ ...getHeaderStyle(isUrduUi), textAlign: "center" });

const formatAmount = (value) =>
  new Intl.NumberFormat().format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : String(value);
};

const camelizeKeys = (value) => {
  if (Array.isArray(value)) return value.map(camelizeKeys);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        key.charAt(0).toLowerCase() + key.slice(1),
        camelizeKeys(nested),
      ]),
    );
  }
  return value;
};

const ManageUsraInfaq = ({ history }) => {
  const { t } = useTranslation();
  const isUrduUi = GetSelectedLanguage() === "ur";
  const unitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const today = new Date();
  const [values, setValues] = useState({
    unit: { value: unitId, lable: "" },
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: new Date(),
    selectedAccounts: [],
    selectedStatuses: [],
  });
  const [items, setItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const headerStyle = getHeaderStyle(isUrduUi);
  const headerCenter = centerHeader(isUrduUi);

  const handleSearch = () => {
    setLoading(true);
    setSearched(true);
    ApiFund()
      .SearchUsraInfaq({
        unitId,
        startDate: convertToDateString(values.startDate),
        endDate: convertToDateString(values.endDate),
        accountIds: (values.selectedAccounts || []).map((a) => a.value),
        statuses: (values.selectedStatuses || []).map((s) => s.value),
      })
      .then((res) => {
        const data = camelizeKeys(res.data || {});
        setItems(data.items || []);
        setTotalAmount(data.totalAmount || 0);
      })
      .catch(() => {
        setItems([]);
        setTotalAmount(0);
      })
      .finally(() => setLoading(false));
  };

  const handleVoid = (row) => {
    swal({
      title: t("common_delete_message"),
      text: t("usra_void_reason"),
      content: "input",
      buttons: true,
      dangerMode: true,
      className: GetSelectedLanguage() === "en" ? "" : "ur",
    }).then((reason) => {
      if (reason === null || reason === false) return;
      if (!String(reason).trim()) {
        swal({
          title: t("reason_required"),
          icon: "error",
          className: GetSelectedLanguage() === "en" ? "" : "ur",
        });
        return;
      }
      setLoading(true);
      ApiFund()
        .VoidUsraInfaq(row.id, { reason: String(reason).trim() })
        .then((res) => {
          if (res.data?.statusCode === "200") {
            handleSearch();
          } else {
            swal({
              title: t(res.data?.message || "void_only_while_held"),
              icon: "error",
              className: GetSelectedLanguage() === "en" ? "" : "ur",
            });
            setLoading(false);
          }
        })
        .catch((err) => {
          swal({
            title: t(err.response?.data?.message || "void_only_while_held"),
            icon: "error",
            className: GetSelectedLanguage() === "en" ? "" : "ur",
          });
          setLoading(false);
        });
    });
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div
          className="ibox-title"
          style={{
            borderTop: "2px solid #1ab394",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h5 style={{ color: "#1a6b5c", fontWeight: 700, marginBottom: 0 }}>
            <GetLanguageString props="manage_infaq" />
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>
        <div className="ibox-content">
          <div
            style={{
              padding: "16px 18px",
              background: "linear-gradient(180deg, #f8fffd 0%, #ffffff 100%)",
              border: "1px solid #e7eaec",
              borderLeft: "4px solid #1ab394",
              borderRadius: 6,
            }}
          >
            <ManageUsraInfaqFilters values={values} setValues={setValues} />
            <div className="text-end" style={{ marginTop: 8 }}>
              <Button color="primary" onClick={handleSearch}>
                <GetLanguageString props="common_search" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {searched && (
        <div className="ibox">
          <div className="ibox-content">
            {loading ? (
              LoadingSpinner()
            ) : items.length === 0 ? (
              <NoResult />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table
                  responsive
                  className="mb-0"
                  style={{ background: "#fff", border: "1px solid #e7eaec" }}
                >
                  <thead>
                    <tr>
                      <th style={headerCenter}>#</th>
                      <th style={headerStyle}>
                        <GetLanguageString props="common_name" />
                      </th>
                      <th style={headerCenter}>
                        <GetLanguageString props="member_report_member_serialNumber" />
                      </th>
                      <th style={headerCenter}>
                        <GetLanguageString props="fund_account_head" />
                      </th>
                      <th style={headerCenter}>
                        <GetLanguageString props="funds_add_fund_amount" />
                      </th>
                      <th style={headerCenter}>
                        <GetLanguageString props="funds_date" />
                      </th>
                      <th style={headerCenter}>
                        <GetLanguageString props="usra_infaq_status" />
                      </th>
                      <th style={headerCenter}>
                        <GetLanguageString props="common_action" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, index) => (
                      <tr
                        key={row.id}
                        style={{
                          borderBottom: "1px solid #d9e2e1",
                          background: index % 2 === 0 ? "#ffffff" : "#e6f3ef",
                        }}
                      >
                        <td style={centerCell}>{index + 1}</td>
                        <td style={cellStyle}>
                          {applyClassToCharacters(row.payerName || "-", "ur")}
                        </td>
                        <td style={centerCell}>{row.serialNumber || "-"}</td>
                        <td style={centerCell}>
                          {applyClassToCharacters(row.infaqAccountName || "-", "ur")}
                        </td>
                        <td style={{ ...centerCell, fontWeight: 600, color: "#2f4050" }}>
                          {formatAmount(row.amount)}
                        </td>
                        <td style={centerCell}>{formatDate(row.date)}</td>
                        <td style={centerCell}>
                          {t(`usra_infaq_status_${row.status}`) || row.status}
                        </td>
                        <td style={centerCell}>
                          {row.receiptToken && (
                            <>
                              <Button
                                id={`usra-receipt-${row.id}`}
                                color="white"
                                size="sm"
                                className="me-1"
                                onClick={() =>
                                  viewUsraReceiptPdf({
                                    receiptToken: row.receiptToken,
                                    t,
                                  })
                                }
                              >
                                <IoReceiptSharp color="#5b7cfa" size={18} />
                              </Button>
                              <UncontrolledTooltip target={`usra-receipt-${row.id}`}>
                                <GetLanguageString props="common_view_receipt" />
                              </UncontrolledTooltip>
                            </>
                          )}
                          {row.canEdit && (
                            <>
                              <Button
                                id={`usra-edit-${row.id}`}
                                color="white"
                                size="sm"
                                className="me-1"
                                onClick={() => setEditItem(row)}
                              >
                                <BiSolidEdit color="#18A689" size={16} />
                              </Button>
                              <UncontrolledTooltip target={`usra-edit-${row.id}`}>
                                <GetLanguageString props="common_edit" />
                              </UncontrolledTooltip>
                            </>
                          )}
                          {row.canDelete && (
                            <>
                              <Button
                                id={`usra-delete-${row.id}`}
                                color="white"
                                size="sm"
                                onClick={() => handleVoid(row)}
                              >
                                <BsTrash3Fill color="#ed5565" size={15} />
                              </Button>
                              <UncontrolledTooltip target={`usra-delete-${row.id}`}>
                                <GetLanguageString props="common_delete" />
                              </UncontrolledTooltip>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot style={{ backgroundColor: "#f3f3f4", borderTop: "2px solid #e7eaec" }}>
                    <tr>
                      <td colSpan={4} style={{ ...cellStyle, fontWeight: 700, color: "#2f4050" }}>
                        <GetLanguageString props="usra_total" />
                      </td>
                      <td style={{ ...centerCell, fontWeight: 700, color: "#1ab394" }}>
                        {formatAmount(totalAmount)}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}

      {editItem && (
        <EditUsraInfaq
          item={editItem}
          setModal={() => setEditItem(null)}
          onSuccess={handleSearch}
        />
      )}
    </div>
  );
};

export default withRouter(ManageUsraInfaq);
